import { useEffect, useState } from 'react';

import { useAuth } from '../../features/Auth/AuthContext';
import { getInitialValues } from '../../features/OfferAdd/hooks/getInitialValues';
import { Offer } from '../../models/offers.model';
import { firebaseFunctions, firestore } from '../firebase';
import { FirebaseCallableFunctions, FirestoreCollection } from '../firebase.models';

import { setProjectOffer } from './setProjectOffer';
import { useProcessDates } from './useProcessDates.hook';

export function useGetProjectOffer(id: string): { offer?: Offer; error?: string; hasPermission?: boolean } {
  const [offer, setOffer] = useState<Offer>();
  const [error, setError] = useState<string>();
  const { currentUser, isAuthenticating } = useAuth();
  const [hasPermission, setHasPermission] = useState<boolean>(undefined);

  useEffect(() => {
    // Skip permission check if still authenticating
    if (isAuthenticating) {
      return;
    }

    // For unauthenticated users, use OFFERS_PUBLIC
    if (!currentUser) {
      setHasPermission(false);
      return;
    }

    firestore
      .collection(FirestoreCollection.OFFERS)
      .doc(id)
      .get()
      .then((snapshot) => {
        if (snapshot.exists) {
          // User can access the document in OFFERS collection
          console.log('DEBUG: User has permission to access OFFERS collection');
          setHasPermission(true);
        } else {
          // Document doesn't exist - this is a new offer, user can create it
          console.log('DEBUG: New offer - user can create it');
          setHasPermission(true);
        }
      })
      .catch(() => {
        // Permission denied by Firestore rules - user can't access OFFERS collection
        // This means they are not owner/collaborator, will use OFFERS_PUBLIC
        console.log('DEBUG: No access to OFFERS collection, will use OFFERS_PUBLIC');
        setHasPermission(false);
      });
  }, [id, currentUser, isAuthenticating]);

  useEffect(() => {
    if (isAuthenticating) return;
    if (hasPermission === undefined) return;

    const uid = currentUser?.uid;

    const docRef = firestore
      .collection(hasPermission ? FirestoreCollection.OFFERS : FirestoreCollection.OFFERS_PUBLIC)
      .doc(id);

    docRef
      .get()
      .then((snapshot) => {
        if (snapshot.exists) {
          const data = snapshot.data();
          setOffer({
            ...data,
            id,
            ...useProcessDates({
              availability: data.availability,
              availabilityTo: data.availabilityTo,
              offerValidTo: data.offerValidTo,
              createdAt: data.createdAt,
              lastModified: data.lastModified,
            }),
          } as Offer);
        } else {
          const newOffer = {
            ...getInitialValues(),
            id: id,
            uid: uid,
          } as Offer;
          const docRef = firestore.collection(FirestoreCollection.OFFERS).doc(id);
          docRef
            .set(newOffer)
            .then(() => {
              setOffer(newOffer);
              setProjectOffer(newOffer);
              const onCreateOffer = firebaseFunctions.httpsCallable(FirebaseCallableFunctions.ON_CREATE_OFFER);
              onCreateOffer({ offerId: id, uid: uid });
            })
            .catch((e) => {
              console.log('Error creating new document', e);
              setError('Wystąpił błąd podczas tworzenia nowego dokumentu. Nie masz do tego uprawnień.');
            });
          setOffer(newOffer);
        }
      })
      .catch((e) => {
        console.log('Error', e);
        setError('Wystąpił błąd podczas pobierania danych. Nie masz do niego dostępu.');
      });
  }, [isAuthenticating, currentUser, id, hasPermission]);

  console.log('OFFER', offer);

  return { offer, error, hasPermission };
}
